// Centralised resolver for the localized Exercise.name (added when the
// schema flipped from plain `string` to `LocalizedText` in shared 1.95.0).
// One resolver, shared by mobile, web and packages/ui: call the hook once at the
// top of a component and apply the returned function where `ex.name` was read.
//
// Search/sort callers that need a STABLE key (independent of locale)
// should reach for `.name.en` directly — the catalog dedup + sort keys
// must not shift when the user toggles language at runtime.

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { catalogBySlug, pickLocalizedText, type Exercise, type LocalizedText } from '@heraion/shared';

import { useLocale } from '../primitives/useLocale';

export function useExerciseName(): (name: LocalizedText) => string {
  const locale = useLocale();
  return (name) => pickLocalizedText(name, locale);
}

// Hook variant that resolves an exercise BY SLUG against the active
// catalog. The architectural rule (Antenore 2026-05-22): exercise names
// are NEVER read from write-time snapshot fields; always resolved via
// foreign key + the live catalog. When the catalog row is missing
// (deleted exercise) the helper falls back to the optional snapshot or
// a localised "Deleted exercise" placeholder. Single source of truth =
// the catalog; rename a row and every historical surface picks up the
// new label without a migration.
export function useExerciseNameBySlug(
  catalog:
    | ReadonlyMap<string, Exercise>
    | ReadonlyArray<Exercise>
    | ReadonlyArray<{ slug: string; name: string }>
    | null
    | undefined,
): (slug: string, snapshotFallback?: string) => string {
  const { t } = useTranslation();
  const locale = useLocale();
  // Memo the catalog Map identity so callers can safely include the
  // returned closure in useMemo/useEffect deps. Re-running when the
  // raw catalog reference changes is enough; rebuilding on every
  // render would force every consumer's downstream memos to bust.
  // Plain `{slug, name: string}` rows (composer CatalogItem) are already
  // resolved strings; they win over the snapshot.
  const map = useMemo<ReadonlyMap<string, { name: LocalizedText | string }> | null>(() => {
    if (catalog instanceof Map) return catalog;
    if (Array.isArray(catalog)) {
      // Per element: a row with a plain string name is kept as-is, a
      // localized one goes through the shared slug map.
      const rows = catalog as ReadonlyArray<{ slug: string; name: LocalizedText | string }>;
      const plain = new Map<string, { name: LocalizedText | string }>();
      const localized: Exercise[] = [];
      for (const c of rows) {
        if (typeof c.name === "string") plain.set(c.slug, c);
        else localized.push(c as Exercise);
      }
      if (plain.size === 0) return catalogBySlug(localized);
      for (const [slug, ex] of catalogBySlug(localized)) plain.set(slug, ex);
      return plain;
    }
    return null;
  }, [catalog]);
  return useCallback(
    (slug, snapshotFallback) => {
      const ex = map?.get(slug);
      if (ex) return typeof ex.name === "string" ? ex.name : pickLocalizedText(ex.name, locale);
      if (snapshotFallback && snapshotFallback.trim().length > 0)
        return snapshotFallback;
      return t('catalog.deletedExercise', {
        defaultValue: 'Deleted exercise',
        slug,
      });
    },
    [map, locale, t],
  );
}

/** "barbell-bench-press" reads "Barbell bench press": the fallback when the
 *  catalog cannot name a slug yet (#182), so a raw slug never shows. */
export const slugAsWords = (slug: string): string => {
  const words = slug.replace(/[-_]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};
